import { ZoomTypes } from 'components/Zoom'
import { ArtboardDetailInfoFragment } from '@sketch/gql-types'

export interface Size {
  width: number
  height: number
}

interface Offset {
  top: number
  left: number
}

type File = NonNullable<NonNullable<ArtboardDetailInfoFragment['files']>[0]>

type ThumbnailType = NonNullable<NonNullable<File['thumbnails']>[0]>['type']

export const scrollOffset = ({
  scaleFactor,
  scaledSize,
  containerSize,
  currentOffset,
  centerPoint,
}: {
  scaleFactor: number
  scaledSize: Size
  containerSize: Size
  currentOffset: Offset
  centerPoint?: Offset
}) => {
  const newOffset: Offset = {
    top: scaledSize.height / 2 - containerSize.height / 2,
    left: scaledSize.width / 2 - containerSize.width / 2,
  }

  centerPoint = centerPoint || {
    left: containerSize.width / 2,
    top: containerSize.height / 2,
  }

  if (scaledSize.width > containerSize.width) {
    const containerCenterX = currentOffset.left + centerPoint.left
    newOffset.left = containerCenterX * scaleFactor - centerPoint.left
  }

  if (scaledSize.height > containerSize.height) {
    const containerCenterY = currentOffset.top + centerPoint.top
    newOffset.top = containerCenterY * scaleFactor - centerPoint.top
  }

  return newOffset
}

export const shrinkToFitFactor = ({
  containerSize,
  itemSize,
}: {
  containerSize: Size
  itemSize: Size
}) => {
  if (
    !containerSize.width ||
    !containerSize.height ||
    !itemSize.width ||
    !itemSize.height
  ) {
    return 1
  }

  return Math.min(
    1,
    Math.min(
      containerSize.width / itemSize.width,
      containerSize.height / itemSize.height
    )
  )
}

export const createSrcSetForFiles = (files: File[]): string | null => {
  if (!files) return null

  return files
    .sort((a: File, b: File): number => {
      if (a.scale && b.scale) return a.scale - b.scale
      return 0
    })
    .map((a: File) => a.url)
    .filter(url => url !== null)
    .map((url, i) => `${url} ${i + 1}x`)
    .join(', ')
}

export const createThumbnailSrcSetForFiles = (
  files: File[],
  type: ThumbnailType = 'L'
): string | null => {
  if (!files) return null

  return files
    .sort((a: File, b: File): number => {
      if (a.scale && b.scale) return a.scale - b.scale
      return 0
    })
    .map((a: File) => {
      const thumbnails = a.thumbnails ?? []
      const largeThumbnail = thumbnails.find(t => t?.type === type) // try to find the correct size
      const thumbnail = largeThumbnail ?? thumbnails[0] // fall back to first available thumbnail

      return thumbnail?.url ?? null
    })
    .filter(url => url !== null)
    .map((url, i) => `${url} ${i + 1}x`)
    .join(', ')
}

interface CursorProps {
  inspectorActive: boolean
  zoom: ZoomTypes
  zoomToFitFactor: number
  isMovingAnnotation?: boolean
  isPlacingAnnotation?: boolean
}

export const artboardContainerCursor = ({
  inspectorActive,
  zoom,
  zoomToFitFactor,
  isMovingAnnotation,
  isPlacingAnnotation,
}: CursorProps) => {
  if (inspectorActive) {
    return 'auto'
  }

  if (isMovingAnnotation) {
    return 'move'
  }

  if (isPlacingAnnotation) {
    return 'not-allowed'
  }

  if (zoom.type === 'ZOOM_TO_FIT' && zoom.factor !== 1) {
    return 'zoom-in'
  }

  if (zoom.factor) {
    if (zoom.factor < zoomToFitFactor) {
      return 'zoom-in'
    } else if (zoom.factor > zoomToFitFactor) {
      return 'zoom-out'
    }
  }

  return 'auto'
}

export const avoidBrowserBackActionWhenPanning = (event: WheelEvent) => {
  const delta = event?.deltaX
  if (!delta) return

  const isLeftEdgeReached =
    (event.currentTarget as HTMLElement).scrollLeft === 0 && delta < 0
  if (isLeftEdgeReached) event.preventDefault()
}
